import { Badge, Dropdown, List, Avatar, Typography, Space, Button, Divider, Empty } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { list } from "cart-localstorage";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import config from "../../../configuration/config";
import { numberFormat } from "../../../utils/currencyFormatter";
import _ from "lodash";
import { Base64 } from "js-base64";
import { Price } from "../../../components/__simple/_price";
import { useDispatch } from "react-redux";
import { changeCount } from "../../../_redux/slices/cartSlice";

const { Text } = Typography;

export default function CartIconWithDropdown({ linkToCart = "/parts/cart" }) {
  const { t: trans, i18n } = useTranslation([`cartFile`]);
  const dispatch = useDispatch();

  const lang = i18n.language || "ar";
  const languageDir = "language-" + lang;

  // ===== Settings =====
  const HOVER_OPEN_DELAY_MS = 200;

  // Controlled dropdown open state
  const [open, setOpen] = useState(false);

  // Cached cart data
  const [cartItems, setCartItems] = useState([]);
  const [fullTotal, setFullTotal] = useState(0);

  // Prevent auto-open on first load
  const didInitRef = useRef(false);

  // Track length changes for "lock open on add only"
  const prevLenRef = useRef(0);

  // Hover timer
  const hoverTimerRef = useRef(null);

  // Touch detection (mobile -> click)
  const [isTouch, setIsTouch] = useState(false);

  // Refs for outside-click close (reliable)
  const triggerRef = useRef(null);
  const overlayRef = useRef(null);

  // ===== Responsive state (width/height) =====
  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 375);
  const [vh, setVh] = useState(typeof window !== "undefined" ? window.innerHeight : 700);

  useEffect(() => {
    const onResize = () => {
      setVw(window.innerWidth);
      setVh(window.innerHeight);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = vw < 768;

  const placement = useMemo(() => {
    if (isMobile) return "bottom";
    return lang === "ar" ? "bottomLeft" : "bottomRight";
  }, [isMobile, lang]);

  useEffect(() => {
    const touch = (typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0)) || false;
    setIsTouch(touch);
  }, []);

  // Hover على الديسكتوب، Click على اللمس/الموبايل
  const trigger = useMemo(() => (isTouch || isMobile ? ["click"] : ["hover"]), [isTouch, isMobile]);

  const dropdownWidth = useMemo(() => {
    if (!isMobile) return 370;
    return Math.min(370, Math.max(280, vw - 24));
  }, [isMobile, vw]);

  const listMaxHeight = useMemo(() => {
    if (!isMobile) return 360;
    return Math.min(280, Math.max(180, Math.round(vh * 0.35)));
  }, [isMobile, vh]);

  const avatarSize = useMemo(() => (isMobile ? 48 : 56), [isMobile]);

  const getCartList = useCallback(() => {
    const types = ["part"];
    const lowerTypes = types.map((t) => t.toLowerCase());
    const items = list() || [];
    return items.filter((item) => lowerTypes.includes(item.Type?.toLowerCase()));
  }, []);

  const computeTotal = useCallback((items) => {
    return _.sum(items.map((p) => (p.price || 0) * (p.quantity || 0)));
  }, []);

  const recalc = useCallback(() => {
    const items = getCartList();
    setCartItems(items);
    setFullTotal(computeTotal(items));
    dispatch(changeCount({ count: items.length }));

    if (items.length === 0) setOpen(false);
    return items;
  }, [computeTotal, dispatch, getCartList]);

  // First load: recalc only (no open)
  useEffect(() => {
    const items = recalc();
    prevLenRef.current = items.length;
    didInitRef.current = true;
  }, [recalc]);

  // Listen to changes (other tab + same tab)
  useEffect(() => {
    const onStorage = () => {
      const before = prevLenRef.current;
      const items = recalc();
      const after = items.length;

      // Lock open only when item ADDED (after init)
      if (didInitRef.current && after > before && after > 0) setOpen(true);

      prevLenRef.current = after;
    };

    const onCartChanged = () => {
      const before = prevLenRef.current;
      const items = recalc();
      const after = items.length;

      if (didInitRef.current && after > before && after > 0) setOpen(true);

      prevLenRef.current = after;
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("cart:changed", onCartChanged);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("cart:changed", onCartChanged);
    };
  }, [recalc]);

  // Clear hover timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  // ✅ Reliable close on outside click
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e) => {
      const t = e.target;

      const triggerEl = triggerRef.current;
      const overlayEl = overlayRef.current;

      const clickedInsideTrigger = triggerEl && triggerEl.contains(t);
      const clickedInsideOverlay = overlayEl && overlayEl.contains(t);

      if (!clickedInsideTrigger && !clickedInsideOverlay) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", onPointerDown, true);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [open]);

  const content = useMemo(() => {
    if (!cartItems.length) {
      return (
        <div style={{ width: dropdownWidth, padding: 12 }}>
          <Empty description={trans("cartFile:Cart_content")} />
        </div>
      );
    }

    return (
      <div className={"" + languageDir} style={{ width: dropdownWidth, padding: 12 }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Text style={{ fontSize: 12 }}>{trans("cartFile:Cart_content")}:</Text>
          <Text style={{ fontSize: 12 }}>
            {cartItems.length} <span style={{ fontSize: 12, marginInlineStart: 6 }}>{trans("new:pieces")}</span>
          </Text>
        </Space>

        <Divider style={{ margin: "12px 0" }} />

        <div style={{ maxHeight: listMaxHeight, overflow: "auto", paddingInlineEnd: 4 }}>
          <List
            itemLayout="horizontal"
            dataSource={cartItems}
            renderItem={(data) => {
              const isBase64 = Base64?.isValid?.(data?.image) == 1;

              const imgSrc = isBase64
                ? data?.image
                  ? "data:image/png;base64," + data?.image
                  : "/assets/images/default.png"
                : data?.image
                  ? config?.mainURL + data?.image
                  : "/assets/images/default.png";

              const title = data.Type === "car" ? (lang === "ar" ? data?.modelTypes : data?.modelTypesEn) : lang === "ar" ? data?.name : data?.nameEn;

              const modelTypes = lang === "ar" ? data?.modelTypes : data?.modelTypesEn;

              return (
                <List.Item style={{ paddingBlock: 10 }}>
                  <List.Item.Meta
                    avatar={<Avatar shape="square" size={avatarSize} src={imgSrc} />}
                    title={
                      <div style={{ fontSize: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <Text style={{ fontSize: 14 }}>
                          {title}
                        </Text>
                        {data?.year ? <Text type="secondary">({data.year})</Text> : null}
                      </div>
                    }
                    description={
                      <div style={{ display: "grid", gap: 4 }}>
                        {data?.brand ? (
                          <Text type="secondary" style={{ fontSize: 15 }}>
                            {trans("cartFile:trade")}: {lang === "ar" ? data?.brand : data?.brandEn}
                          </Text>
                        ) : null}

                        {data?.Type !== "car" && modelTypes ? (
                          <Text type="secondary" style={{ fontSize: 15 }}>
                            {trans("cartFile:vehicle_type")}: {modelTypes}
                          </Text>
                        ) : null}

                        {data?.colorName ? (
                          <Text type="secondary" style={{ fontSize: 15 }}>
                            {trans("cartFile:color")}: {lang === "ar" ? data?.colorName : data?.colorNameEn}
                          </Text>
                        ) : null}

                        <Space size={8} wrap>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {trans("cartFile:product_price")}:
                          </Text>
                          <Text style={{ fontSize: 15 }}>
                            <Price price={data?.price} />
                          </Text>
                        </Space>
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </div>

        {fullTotal ? (
          <>
            <Divider style={{ margin: "12px 0" }} />
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <Text style={{ fontSize: 12 }}>
                {trans("new:total")}{" "}
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {trans("new:total_include_tax")}
                </Text>
              </Text>
              <Text style={{ fontSize: 14 }}>{numberFormat(fullTotal)}</Text>
            </Space>
          </>
        ) : null}

        <Divider style={{ margin: "12px 0" }} />

        <Space direction="vertical" style={{ width: "100%" }} size={10}>
          <Button block onClick={() => setOpen(false)}>
            {trans("cartFile:Continue_browsing")}
          </Button>

          <Link to={linkToCart} onClick={() => setOpen(false)} style={{ width: "100%" }}>
            <Button
              style={{
                background: "#00b362",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(0, 179, 98, 0.3)",
              }}

              type="primary" block>
              {trans("cartFile:View_cart")}
            </Button>
          </Link>
        </Space>
      </div>
    );
  }, [avatarSize, cartItems, dropdownWidth, fullTotal, lang, linkToCart, listMaxHeight, trans, languageDir]);

  return (
    <div className={"mx-5  " + languageDir}>
      <Dropdown
        placement={placement}
        trigger={trigger}
        open={open && cartItems.length > 0}
        dropdownRender={() => (
          <div
            ref={overlayRef}
            style={{
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              overflow: "hidden",
              maxWidth: "calc(100vw - 12px)",
            }}>
            {content}
          </div>
        )}
        onOpenChange={(next, info) => {
          if (!cartItems.length) {
            setOpen(false);
            return;
          }

          if (!didInitRef.current && next === true) return;

          // Mobile/Touch: normal click open/close
          if (isTouch || isMobile) {
            setOpen(next);
            return;
          }

          // Desktop hover + delay + lock open (close only by outside click handler)
          if (info?.source === "trigger") {
            if (next === true) {
              if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
              hoverTimerRef.current = setTimeout(() => setOpen(true), HOVER_OPEN_DELAY_MS);
            } else {
              // ignore hover-leave close
              if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
            }
            return;
          }
        }}>
        <span ref={triggerRef} style={{ display: "inline-flex" }}>
          <Link to={linkToCart} style={{ display: "inline-flex", alignItems: "center" }}>
            <Badge
              className="parts-cart-badge"
              count={cartItems.length}
              overflowCount={99}
              offset={lang === "ar" ? [8, 8] : [-8, 8]}
            >
              <div className="parts-cart-icon-wrapper">
                <img src="/assets/icons/parts-cart.svg" alt="parts cart" />
              </div>
            </Badge>
          </Link>
        </span>
      </Dropdown>
    </div>
  );
}
